import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react'

import { useAssetNames } from '../../hooks/useAssetNames'
import { usePresentationResolver } from '../../hooks/usePresentationResolver'
import { usePresentationSync } from '../../hooks/usePresentationSync'
import { indexOfSlide, presentationSlides, storySpans, useKeepSlideInPlace, usePresenter } from '../../hooks/usePresenter'
import * as presentationService from '../../services/presentationService'
import type { Presentation } from '../../types/presentation.types'
import { PresentedSlideView } from './PresentedSlideView'

/**
 * The presenter's own window (MYD-497), opened from the presenter with P.
 * What the audience does not see: the next slide, large speaker notes, an
 * elapsed timer and the clock.
 *
 * It follows the audience window, and drives it: a move in either is a move
 * in both (usePresentationSync). Esc does nothing here on purpose; ending the
 * show from the notes window by accident would be the worst kind of fumble.
 */

const pad = (value: number) => String(value).padStart(2, '0')

/** "12:07", or "1:02:07" past the hour. */
export const formatElapsed = (ms: number) => {
	const seconds = Math.max(0, Math.floor(ms / 1000))
	const hours = Math.floor(seconds / 3600)
	const minutes = Math.floor((seconds % 3600) / 60)
	return hours ? `${hours}:${pad(minutes)}:${pad(seconds % 60)}` : `${minutes}:${pad(seconds % 60)}`
}

export function PresenterView() {
	const { id = '' } = useParams()
	const [presentation, setPresentation] = useState<Presentation | null>(null)
	const [status, setStatus] = useState<'loading' | 'failed' | 'ready'>('loading')
	const [ended, setEnded] = useState(false)
	const [startedAt, setStartedAt] = useState(() => Date.now())
	const [now, setNow] = useState(() => Date.now())
	const [width, setWidth] = useState(0)
	const currentBox = useRef<HTMLDivElement>(null)

	const deck = presentation?.definition ?? null
	// As in PresenterScreen: the list depends on what has resolved, and what
	// to resolve on the story on screen, so the story is held in state.
	const [currentStory, setCurrentStory] = useState<number | null>(null)
	const wanted = useMemo(() => (currentStory === null ? [] : [currentStory, currentStory + 1]), [currentStory])
	const resolver = usePresentationResolver(presentation ? id : null, deck, wanted)
	const slides = useMemo(() => (deck ? presentationSlides(deck, resolver.blocks) : []), [deck, resolver.blocks])
	const spans = useMemo(() => storySpans(slides), [slides])
	const presenter = usePresenter(slides.length)
	useKeepSlideInPlace(slides, presenter)
	const storyOnScreen = slides[presenter.index]?.storyIndex ?? null
	useEffect(() => setCurrentStory(storyOnScreen), [storyOnScreen])
	const assetName = useAssetNames(deck ? deck.stories.map(story => (story.divider ? story.anchor : null)) : [])

	useEffect(() => {
		let cancelled = false
		presentationService
			.getPresentation(id)
			.then(loaded => {
				if (cancelled) return
				setPresentation(loaded)
				setStatus('ready')
				document.title = `Presenter view: ${loaded.name}`
			})
			.catch(() => !cancelled && setStatus('failed'))
		return () => {
			cancelled = true
		}
	}, [id])

	usePresentationSync({
		presentationId: id,
		index: presenter.index,
		slideKey: slides[presenter.index]?.slide.id ?? null,
		enabled: slides.length > 0,
		onRemoteGoto: (index, key) => {
			const to = indexOfSlide(slides, key) ?? index
			setEnded(false)
			presenter.go(to, to === presenter.index + 1 ? 'next' : to === presenter.index - 1 ? 'previous' : 'jump')
		},
		onEnded: () => setEnded(true),
	})

	// The clock and the timer tick once a second.
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [])

	// The current slide fills its column; everything else is sized from it.
	useLayoutEffect(() => {
		const box = currentBox.current
		if (!box) return
		// Before layout (or in a test DOM) the box measures 0; draw at a
		// reasonable size rather than not at all.
		const measure = () => setWidth(Math.floor(box.getBoundingClientRect().width) || 640)
		measure()
		window.addEventListener('resize', measure)
		return () => window.removeEventListener('resize', measure)
	}, [status])

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey || event.ctrlKey || event.altKey) return
			// Moving only. Panels, notes and exiting belong to the audience window.
			if (['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(event.key)) {
				event.preventDefault()
				presenter.next()
			} else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
				event.preventDefault()
				presenter.previous()
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [presenter])

	if (status === 'loading') return <Dark>Opening...</Dark>
	if (status === 'failed' || !deck) return <Dark>This presentation could not be opened.</Dark>
	if (slides.length === 0) return <Dark>This presentation has no slides yet.</Dark>

	const current = slides[presenter.index]
	const upcoming = slides[presenter.index + 1]
	const story = spans.find(span => span.storyIndex === current.storyIndex)
	const notes = current.divider ? '' : current.slide.speakerNotes
	const clock = new Date(now).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

	return (
		<div className="flex h-[100dvh] flex-col bg-neutral-950 text-white" data-testid="presenter-view">
			<header className="flex items-center gap-6 border-b border-white/10 px-6 py-3">
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold">{presentation?.name}</p>
					<p className="truncate text-xs text-white/60">
						Slide {presenter.index + 1} of {slides.length}
						{story && ` · Story ${spans.indexOf(story) + 1} of ${spans.length}: ${story.title}`}
					</p>
				</div>
				<div className="flex items-center gap-2 text-2xl font-semibold tabular-nums" aria-label="Elapsed time">
					{formatElapsed(now - startedAt)}
					<button
						type="button"
						onClick={() => {
							setStartedAt(Date.now())
							setNow(Date.now())
						}}
						className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"
						aria-label="Reset timer"
						title="Reset timer"
					>
						<RotateCcw className="h-4 w-4" />
					</button>
				</div>
				<div className="text-2xl tabular-nums text-white/70" aria-label="Clock">
					{clock}
				</div>
			</header>

			{ended && (
				<p className="bg-warning/20 px-6 py-2 text-sm text-white" role="status">
					The presentation has ended in the other window. It will pick up again if it restarts.
				</p>
			)}

			<main className="grid min-h-0 flex-1 grid-cols-[3fr_2fr] gap-6 p-6">
				<section className="flex min-h-0 flex-col gap-2">
					<h2 className="text-xs font-semibold uppercase tracking-wide text-white/50">Now showing</h2>
					<div ref={currentBox} className="w-full">
						{width > 0 && (
							<PresentedSlideView entry={current} width={width} blocks={resolver.blocks} theme={deck.theme} storyCount={deck.stories.length} assetName={assetName} className="overflow-hidden rounded-md" />
						)}
					</div>
				</section>

				<section className="flex min-h-0 flex-col gap-4">
					<div>
						<h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">Next</h2>
						{upcoming ? (
							width > 0 && (
								<PresentedSlideView
									entry={upcoming}
									width={Math.floor(width * 0.6)}
									blocks={resolver.blocks}
									theme={deck.theme}
									storyCount={deck.stories.length}
									assetName={assetName}
									className="overflow-hidden rounded-md opacity-90"
								/>
							)
						) : (
							<p className="text-sm text-white/50">End of the presentation.</p>
						)}
					</div>

					<div className="flex min-h-0 flex-1 flex-col">
						<h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">Notes</h2>
						<div className="min-h-0 flex-1 overflow-y-auto rounded-md bg-white/5 p-4 text-2xl leading-relaxed" aria-label="Speaker notes">
							{notes.trim() ? <p className="whitespace-pre-wrap">{notes}</p> : <p className="text-white/40">No notes for this slide.</p>}
						</div>
					</div>
				</section>
			</main>

			<footer className="flex items-center justify-center gap-4 border-t border-white/10 px-6 py-3">
				<button
					type="button"
					onClick={presenter.previous}
					disabled={presenter.index === 0}
					className="inline-flex items-center gap-1 rounded-full bg-white/10 px-5 py-2 text-sm font-medium hover:bg-white/20 disabled:opacity-30"
				>
					<ChevronLeft className="h-4 w-4" /> Previous
				</button>
				<button
					type="button"
					onClick={presenter.next}
					disabled={presenter.index === slides.length - 1}
					className="inline-flex items-center gap-1 rounded-full bg-primary px-6 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-30"
				>
					Next <ChevronRight className="h-4 w-4" />
				</button>
			</footer>
		</div>
	)
}

function Dark({ children }: { children: React.ReactNode }) {
	return <div className="flex h-[100dvh] items-center justify-center bg-neutral-950 text-white">{children}</div>
}

export default PresenterView
